import React, {useMemo} from 'react'
import {useTranslation} from 'i18n'
import {parseShape} from 'types'
import {ThemeColor, useTheme} from 'themes'
import {useWindowMode} from 'windows'
import {getFontFamily, getFontSize, useHistory} from 'utils'
import {ParsedText} from 'common/components'
import {useSelector} from 'lib/redux'
import convertUnit from 'lib/unit'

interface GiftshopFilterRegencyChangeProps {
  allowRegencyChange?: boolean
  regencyTextAlwaysShrink?: boolean
  regencyTextColor?: ThemeColor
  regencyTextMaxLength?: number
  regencyTextMaxLengthBeforeTextShrink?: number
}

export function GiftshopFilterRegencyChange({
  allowRegencyChange,
  regencyTextAlwaysShrink,
  regencyTextColor = 'black',
  regencyTextMaxLength = 30,
  regencyTextMaxLengthBeforeTextShrink = 20,
}: GiftshopFilterRegencyChangeProps) {
  const {translate} = useTranslation()
  const history = useHistory()
  const {regency} = useSelector('exploreFilter')
  const theme = useTheme()
  const mode = useWindowMode()

  const textNeedsToShrink = useMemo(() => {
    const len = regency?.name.length ?? 0
    return mode !== 'website' && len >= regencyTextMaxLengthBeforeTextShrink
  }, [mode, regency?.name.length, regencyTextMaxLengthBeforeTextShrink])

  const regencyText = useMemo(() => {
    if (!regency) return translate('giftShop:exploreFilterRegencyAllCities')
    const {name} = regency

    if (mode !== 'website' && name.length >= regencyTextMaxLength) {
      return `${name.slice(0, regencyTextMaxLength)}...`
    }

    return name
  }, [mode, regency, regencyTextMaxLength, translate])

  const regencyTextProps = useMemo(
    () =>
      regencyTextAlwaysShrink || textNeedsToShrink
        ? textNeedsToShrink
          ? {
              fontSize: getFontSize('xs'),
              lineHeight: convertUnit(10),
            }
          : {
              fontSize: getFontSize('s'),
              lineHeight: convertUnit(12),
            }
        : {
            fontSize: getFontSize('m'),
            lineHeight: convertUnit(14),
          },
    [regencyTextAlwaysShrink, textNeedsToShrink],
  )

  const regencyChangeTextSize = useMemo(
    () => (regencyTextAlwaysShrink || textNeedsToShrink ? 'xxs' : 'xs'),
    [regencyTextAlwaysShrink, textNeedsToShrink],
  )

  const parseText: parseShape[] = useMemo(
    () => [
      {
        pattern: /<at>(.*?)<\/at>/,
        style: {
          color: 'inherit',
          fontFamily: getFontFamily('quicksandMedium'),
          ...regencyTextProps,
        },
        renderText: (str) => str.replace(/<at>|<\/at>/g, ''),
      },
      {
        pattern: /<name>(.*?)<\/name>/,
        style: {
          color: 'inherit',
          fontFamily: getFontFamily('quicksandBold'),
          ...regencyTextProps,
        },
        renderText: (str) => str.replace(/<name>|<\/name>/g, ''),
      },
      {
        pattern: /<change>(.*?)<\/change>/,
        style: {
          color: theme.gray_5,
          fontFamily: getFontFamily('medium'),
          fontSize: getFontSize(regencyChangeTextSize),
        },
        renderText: (str) => str.replace(/<change>|<\/change>/g, ''),
      },
    ],
    [regencyChangeTextSize, regencyTextProps, theme],
  )

  const text = useMemo(
    () =>
      allowRegencyChange
        ? `<at>${translate('global:at').toLowerCase()} </at><name>${
            regency && allowRegencyChange
              ? regencyText
              : translate('giftShop:exploreFilterRegencyAllCities')
          } </name><change>${translate('global:change')}</change>`
        : `<at>${translate('global:at').toLowerCase()} </at><name>${translate(
            'giftShop:exploreFilterRegencyAllCities',
          )} </name><change></change>`,
    [allowRegencyChange, regency, regencyText, translate],
  )

  return (
    <ParsedText
      style={{
        cursor: allowRegencyChange ? 'pointer' : undefined,
        wordBreak: 'break-word',
        wordWrap: 'break-word',
        color: theme[regencyTextColor],
      }}
      parse={parseText}
      onClick={() => {
        if (!allowRegencyChange) return

        history.push('giftshop_filter_regency', {})
      }}>
      {text}
    </ParsedText>
  )
}
